import { WHAT_WE_DO } from '../../utils/constants';
import { getRoutePaths, useLang } from '../../utils/i18n';
import './Main.css';
import React from 'react';
import { Link } from 'react-router-dom';
import ClientLogos from '../ClientLogos/ClientLogos';

const Main = () => {
  const lang = useLang();
  const paths = getRoutePaths(lang);
  const whatWeDo = WHAT_WE_DO[lang];
  const heroStrategy = lang === "en"
    ? [
        { label: "Clarity", detail: "Financial visibility" },
        { label: "Action", detail: "Decide with confidence" },
        { label: "Growth", detail: "Sustainable performance" },
      ]
    : [
        { label: "Clareza", detail: "Visibilidade financeira" },
        { label: "Ação", detail: "Decidir com confiança" },
        { label: "Crescimento", detail: "Performance sustentável" },
      ];


  return (
    <>
    <section className="what-we-do hero-workflow">
        <div className="hero-top">
          <h1 className="main-heading main-animate" style={{ '--delay': '80ms' }}>
            {lang === "en"
              ? "Finance that moves business forward."
              : "Finanças que impulsionam negócios."}
          </h1>
          <p className="hero-intro main-animate" style={{ '--delay': '140ms' }}>
            {lang === "en"
              ? "Strategic finance, data and executive advisory for companies turning complexity into clarity, confident decisions and sustainable growth."
              : "Finanças estratégicas, dados e consultoria executiva para empresas que transformam complexidade em clareza, decisões seguras e crescimento sustentável."}
          </p>
          <div className="hero-actions main-animate" style={{ '--delay': '190ms' }}>
            <Link className="hero-action hero-action-primary" to={paths.services}>
              {lang === "en" ? "Explore our services" : "Conheça nossos serviços"}
            </Link>
            <Link className="hero-action hero-action-secondary" to={paths.contact}>
              {lang === "en" ? "Start a conversation" : "Vamos conversar"}
            </Link>
          </div>
        </div>
        <aside
          className="hero-strategy desktop-only main-animate"
          style={{ '--delay': '230ms' }}
          aria-label={lang === "en" ? "Our strategic path" : "Nossa jornada estratégica"}
        >
          <div className="hero-strategy-line" aria-hidden="true" />
          {heroStrategy.map((step, index) => (
            <div className="hero-strategy-step" key={step.label}>
              <span className="hero-strategy-number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="hero-strategy-node" aria-hidden="true" />
              <span className="hero-strategy-copy">
                <strong>{step.label}</strong>
                <span>{step.detail}</span>
              </span>
            </div>
          ))}
        </aside>
        <div className="hero-scroll-cue desktop-only" aria-hidden="true">
          <span>{lang === "en" ? "Scroll to explore" : "Role para explorar"}</span>
          <span className="hero-scroll-arrow">↓</span>
        </div>
      </section>

     <section className="what-we-do desktop-only what-we-do-details">
      <div className="what-we-do-header main-animate" style={{ '--delay': '120ms' }}>
        <h2 className="what-we-do-title">{whatWeDo.title}</h2>
        <p className="what-we-do-subtitle">{whatWeDo.subtitle}</p>
      </div>
      <div className="what-we-do-grid">
        {whatWeDo.sections.map((section, index) => (
          <React.Fragment key={section.title}>
          {index % 2 === 0 ? 
          <div className="section-card main-animate" style={{ '--delay': `${200 + index * 140}ms` }}>
            <div className="section-image main-animate" style={{ '--delay': `${260 + index * 140}ms` }}>
              <img src={section.image} alt={section.title} loading="lazy" />
            </div>
            <div className="section-content-right main-animate" style={{ '--delay': `${320 + index * 140}ms` }}>
              <h2 className="section-title">{section.title}</h2>
              <p className="section-text">{section.content}</p>
            </div>
          </div>
          :
          <div className="section-card main-animate" style={{ '--delay': `${200 + index * 140}ms` }}>

            <div className="section-content-left main-animate" style={{ '--delay': `${320 + index * 140}ms` }}>
              <h2 className="section-title">{section.title}</h2>
              <p className="section-text">{section.content}</p>
            </div>
            <div className="section-image main-animate" style={{ '--delay': `${260 + index * 140}ms` }}>
              <img src={section.image} alt={section.title} loading="lazy" />
            </div>
          </div>

          }
          </React.Fragment>
        ))}
      </div>
    </section>

    <section className="what-we-do mobile-only what-we-do-details">
      <div className="what-we-do-header main-animate" style={{ '--delay': '120ms' }}>
        <h2 className="what-we-do-title">{whatWeDo.title}</h2>
        <p className="what-we-do-subtitle">{whatWeDo.subtitle}</p>
      </div>
      <div className="what-we-do-grid">
        {whatWeDo.sections.map((section, index) => (
          <React.Fragment key={section.title}>
            
            <div className="section-image main-animate" style={{ '--delay': `${200 + index * 140}ms` }}>
              <img src={section.image} alt={section.title} loading="lazy" />
            </div>
            <div className="section-content-right main-animate" style={{ '--delay': `${260 + index * 140}ms` }}>
              <h2 className="section-title">{section.title}</h2>
              <p className="section-text">{section.content}</p>
            </div>
          </React.Fragment>
        ))}
      </div>


      </section>

    <section className="who-we-are main-animate" style={{ '--delay': '240ms' }}>
        <h2 className='who-we-are-title main-animate' style={{ '--delay': '300ms' }}>
          {lang === "en" ? "Who We Are" : "Quem Somos"}
        </h2>
        <div className='who-we-are-description main-animate' style={{ '--delay': '360ms' }}>
          <p className='who-we-are-text'>
            {lang === "en"
              ? "At Monetique, we combine strategic finance, technology and leadership vision to deliver real, measurable transformation. Our approach blends data led clarity with human understanding because growth only happens when numbers and people move forward together."
              : "Na Monetique, unimos finanças estratégicas, tecnologia e visão de liderança para entregar transformação real e mensurável. Nossa abordagem combina clareza orientada por dados com compreensão humana, porque o crescimento só acontece quando números e pessoas avançam juntos."}
          </p>
          <Link
            className='who-we-are-link'
            to={paths.about}
          >
            {lang === "en" ? "About us" : "Sobre nós"}
          </Link>
        </div>
    </section>
    <div className="home-clients">
      <ClientLogos />
    </div>
    </>
  );
};

export default Main;
